import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Video, Mic, Camera, Monitor } from 'lucide-react';

const ctaButtons = [
  {
    icon: Calendar,
    text: "Book & Schedule",
    onClick: () => window.open('https://calendly.com/udaadu', '_blank')
  },
  {
    icon: Building,
    text: "What Fits My Lot?",
    onClick: () => window.open('/landing', '_self')
  },
  {
    icon: DollarSign,
    text: "Get Price & ROI Report",
    onClick: () => window.open('/adu-calculator', '_self')
  },
  {
    icon: Home,
    text: "Our Portfolio",
    onClick: () => window.open('/our-work', '_self')
  },
  {
    icon: HelpCircle,
    text: "Our FAQ",
    onClick: () => window.open('/faq', '_self')
  }
];

const HowToUseADUAsContentCreatorStudio = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-content-creator-studio" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Build a Studio That Streams Success
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Whether you're a YouTuber, TikToker, podcaster, or course creator, your backyard ADU can become your content HQ—with the lighting, sound, and setup to match.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2 text-white"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Ideal for:</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Video className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Livestreaming or pre-recorded video</span>
                </div>
                <div className="flex items-center gap-3">
                  <Monitor className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Product demos and tutorials</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mic className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Podcast episodes and interviews</span>
                </div>
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Online courses and education content</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1560472355-536de3962603?auto=format&fit=crop&w=800&h=400" 
                alt="Content Creator Studio ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features to Include Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Features to Include</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Audio & Visual Setup</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Acoustic panels and blackout curtains</li>
                <li>• Tripod-ready flooring and wall mounts</li>
                <li>• Softbox or LED lighting racks</li>
                <li>• Green screen or backdrop wall</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Workspace Essentials</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Desk space with monitor arm and mic boom</li>
                <li>• Cable management and power outlets</li>
                <li>• Equipment storage solutions</li>
                <li>• Climate control for electronics</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Monetization & Workflow ROI</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Financial Benefits</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Save on studio rent</li>
                <li>• Tax write-offs for content equipment</li>
                <li>• More content = more revenue streams</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">Creative Control</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Full production control</li>
                <li>• 24/7 access to your studio</li>
                <li>• Custom branding opportunities</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Ready to Build Your Content Empire?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Transform your backyard into a professional content creation studio that pays for itself.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2 text-white"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsContentCreatorStudio;